import { useCallback, useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2, LifeBuoy, ArrowUpRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { appApi } from "@/lib/appApi";
import { useAuth } from "@/context/AuthContext";

function parseMessages(data) {
  const arr = data?.messages || data?.thread?.messages || (Array.isArray(data) ? data : []);
  return (arr || []).map((m) => ({
    text: m.text || m.message || m.body || "",
    sender: (m.sender || m.role || m.from || m.author || "agent").toString().toLowerCase(),
    ts: m.created_at || m.ts || m.time || "",
  }));
}

export default function SupportChat() {
  const { token, user, openAuth } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState([]);
  const [info, setInfo] = useState("");
  const [text, setText] = useState("");
  const bottomRef = useRef(null);

  const load = useCallback(async () => {
    if (!token) { setInfo("Sign in to start a support conversation."); setMessages([]); return; }
    setLoading(true); setInfo("");
    try {
      const d = await appApi("/api/support/thread", { token });
      setMessages(parseMessages(d));
    } catch (e) {
      setInfo(e.message || "Support is unavailable right now.");
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    const onOpen = () => { setOpen(true); load(); };
    window.addEventListener("open-support", onOpen);
    return () => window.removeEventListener("open-support", onOpen);
  }, [load]);

  useEffect(() => { if (open) load(); }, [open, load]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, info]);

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    if (!token) { openAuth("login"); return; }
    setSending(true);
    setMessages((m) => [...m, { text: t, sender: "user", ts: "" }]);
    setText("");
    try {
      await appApi("/api/support/message", { method: "POST", token, body: { text: t } });
      await load();
    } catch (e) {
      setInfo(e.message || "Couldn't send. Please try again in a moment.");
    } finally {
      setSending(false);
    }
  };

  const escalate = async () => {
    if (!token) { openAuth("login"); return; }
    try {
      await appApi("/api/support/escalate", { method: "POST", token });
      toast.success("Connecting you with a human agent — they'll take over from here.");
      load();
    } catch (e) {
      toast.error(e.message || "Could not escalate");
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="support-fab"
        aria-label="Help & Support"
        className="fixed bottom-20 right-5 z-[70] grid h-14 w-14 place-items-center rounded-full bg-primary text-primary-foreground shadow-xl transition-transform hover:scale-105"
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>

      {open && (
        <div
          data-testid="support-panel"
          className="fixed bottom-36 right-5 z-[70] flex h-[70vh] max-h-[560px] w-[92vw] max-w-sm flex-col overflow-hidden rounded-2xl border border-border bg-background shadow-2xl"
        >
          <div className="flex items-center gap-2 border-b border-border bg-card/60 px-4 py-3">
            <span className="grid h-8 w-8 place-items-center rounded-full bg-gold/15 text-gold"><Sparkles className="h-4 w-4" /></span>
            <div className="leading-tight">
              <p className="font-display text-sm font-bold">Vera · Support</p>
              <p className="text-[11px] text-muted-foreground">Chat with Vera — a human can join anytime</p>
            </div>
            <button onClick={escalate} data-testid="support-escalate" className="ml-auto inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-[11px] font-medium hover:border-gold/40 hover:text-gold">
              <ArrowUpRight className="h-3 w-3" /> Human
            </button>
          </div>

          <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
            {loading && <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>}
            {!loading && messages.length === 0 && !info && (
              <p className="text-center text-sm text-muted-foreground">Ask Vera anything about your account, plans or receipts. If she can't resolve it, she'll bring in a human agent.</p>
            )}
            {messages.map((m, i) => {
              const mine = ["user", "customer", "me", "you"].includes(m.sender);
              return (
                <div key={`${m.ts || "m"}-${i}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm ${mine ? "bg-primary text-primary-foreground" : "border border-border bg-card/60"}`}>
                    {!mine && <span className="mb-0.5 block text-[10px] font-semibold uppercase tracking-wide text-gold">{m.sender === "user" ? "" : m.sender}</span>}
                    {m.text}
                  </div>
                </div>
              );
            })}
            {info && (
              <div className="rounded-xl border border-gold/30 bg-gold/10 p-3 text-sm text-foreground/90">
                <LifeBuoy className="mb-1 inline h-4 w-4 text-gold" /> {info}
                {!token && (
                  <button onClick={() => openAuth("login")} className="mt-2 block font-semibold text-gold hover:underline">Log in</button>
                )}
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          <div className="border-t border-border p-3">
            <div className="flex items-center gap-2">
              <input
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder={user ? "Type your message…" : "Sign in to chat"}
                data-testid="support-input"
                className="flex-1 rounded-full border border-border bg-card/50 px-4 py-2.5 text-sm outline-none focus:border-gold/40"
              />
              <button onClick={send} disabled={sending} data-testid="support-send" className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground disabled:opacity-60">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
